import { useTranslation } from "react-i18next";
import {
  useForm,
  SubmitHandler,
  useFormContext,
  FormProvider,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { InputText, Text, Button } from "@pentabd/ui";

import { FORM_FIELDS } from "@constants/form";

import {
  emergencyContactValidation,
  EmergencyContactFormDataType,
} from "@validations/HRM/employee/familyInfoValidation";
import { EmergencyContactType } from "@type/HRM/family";

interface EmergencyContactPropsType {
  emergencyContactInfo?: EmergencyContactType;
  changeEmergencyContactInfo?: any;
  request?: boolean;
  updateEmergencyContactRequest?: boolean;
}

const {
  FAMILY_INFO: { EMERGENCY_CONTACT },
} = FORM_FIELDS.EMPLOYEE;

const EmergencyContactForm = () => {
  const { t } = useTranslation();

  const {
    register,
    formState: { errors },
  } = useFormContext<EmergencyContactFormDataType>();

  return (
    <div className="d-flex flex-column gap-8 pt-10">
      <Text weight="semibold" color="title" size="lg">
        {t("EMPLOYEE.FAMILY_INFO.FORM.EMERGENCY_CONTACT_INFO.HEADING")}
      </Text>

      <div className="d-flex flex-column gap-8">
        <div className="row">
          <div className="col-3">
            <Text weight="semibold" color="title" size="sm">
              {t("EMPLOYEE.FAMILY_INFO.FORM.EMERGENCY_CONTACT_INFO.NAME.LABEL")}
            </Text>
          </div>
          <div className="col-9 col-lg-7">
            <InputText
              {...register(EMERGENCY_CONTACT.NAME)}
              placeholder={t(
                "EMPLOYEE.FAMILY_INFO.FORM.EMERGENCY_CONTACT_INFO.NAME.PLACEHOLDER",
              )}
              error={errors as any}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-3">
            <Text weight="semibold" color="title" size="sm">
              {t(
                "EMPLOYEE.FAMILY_INFO.FORM.EMERGENCY_CONTACT_INFO.RELATION.LABEL",
              )}
            </Text>
          </div>
          <div className="col-9 col-lg-7">
            <InputText
              {...register(EMERGENCY_CONTACT.RELATION)}
              placeholder={t(
                "EMPLOYEE.FAMILY_INFO.FORM.EMERGENCY_CONTACT_INFO.RELATION.PLACEHOLDER",
              )}
              error={errors as any}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-3">
            <Text weight="semibold" color="title" size="sm">
              {t(
                "EMPLOYEE.FAMILY_INFO.FORM.EMERGENCY_CONTACT_INFO.CONTACT.LABEL",
              )}
            </Text>
          </div>
          <div className="col-9 col-lg-7">
            <InputText
              {...register(EMERGENCY_CONTACT.CONTACT)}
              placeholder={t(
                "EMPLOYEE.FAMILY_INFO.FORM.EMERGENCY_CONTACT_INFO.CONTACT.PLACEHOLDER",
              )}
              error={errors as any}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const EmergencyContact = ({
  emergencyContactInfo,
  changeEmergencyContactInfo,
  updateEmergencyContactRequest,
}: EmergencyContactPropsType) => {
  const { t } = useTranslation();

  const methods = useForm<EmergencyContactType>({
    resolver: yupResolver(emergencyContactValidation),
    values: emergencyContactInfo as EmergencyContactType,
  });

  const { handleSubmit } = methods;
  const onSubmit: SubmitHandler<EmergencyContactType> = (
    data: EmergencyContactType,
  ) => {
    changeEmergencyContactInfo(data);
  };

  return (
    <FormProvider {...methods}>
      <form className="container" onSubmit={handleSubmit(onSubmit)}>
        <EmergencyContactForm />

        <div className="row py-10">
          <div className="text-end col-12 col-lg-10">
            <Button
              key="emergency_contact_info_save_bottom"
              loading={updateEmergencyContactRequest}
              htmlType="submit"
              fill="outline"
              type="primary"
              size="sm"
            >
              {t("EMPLOYEE.FAMILY_INFO.FORM.SAVE_BUTTON")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default EmergencyContact;
